
import { supabase } from '@/integrations/supabase/client';
import { SUPABASE_URL } from '@/integrations/supabase/client';

interface TokenRequest {
  providerId: string;
  code?: string;
  credentials?: {
    username?: string;
    password?: string;
    applicationId?: string;
    identityId?: string;
    [key: string]: any;
  };
  redirectUri?: string;
}

interface ApiRequest {
  providerId: string;
  endpointId?: string;
  path?: string;
  method?: string;
  params?: Record<string, any>;
  body?: any;
}

export const apiClient = {
  /**
   * Get an access token for a provider.
   * For OAuth2, provides the code and redirectUri.
   * For custom tokens, provides credentials.
   */
  async getToken(request: TokenRequest): Promise<any> {
    const { data, error } = await supabase.functions.invoke('api-auth', {
      body: request,
    });

    if (error) throw new Error(error.message || 'Failed to get token');
    return data;
  },

  /**
   * Make an API request to a provider endpoint.
   * Can specify either endpointId (to use a pre-configured endpoint)
   * or path/method for a custom request.
   */
  async makeRequest(request: ApiRequest): Promise<any> {
    const { data, error } = await supabase.functions.invoke('api-request', {
      body: request,
    });

    if (error) throw new Error(error.message || 'API request failed');
    return data;
  },

  /**
   * Generate an OAuth2 authorization URL for a provider.
   */
  getOAuth2AuthUrl(provider: any, redirectUri: string, state?: string): string {
    if (!provider || provider.auth_type !== 'oauth2' || !provider.auth_url) {
      throw new Error('Invalid provider for OAuth2');
    }

    const url = new URL(provider.auth_url);
    url.searchParams.append('client_id', provider.client_id);
    url.searchParams.append('redirect_uri', redirectUri);
    url.searchParams.append('response_type', 'code');
    
    if (provider.scope) {
      url.searchParams.append('scope', provider.scope);
    }
    
    if (state) {
      url.searchParams.append('state', state);
    }

    return url.toString();
  },

  /**
   * Generate a 24Seven Office authentication URL
   */
  get24SevenAuthUrl(): string {
    return 'https://id.24sevenoffice.com/Authentication/Login/Login';
  }
};
